/* eslint-disable no-nested-ternary */
import { compose } from '@doltech/utils/lib/compose';
import { isEmpty } from '@doltech/utils/lib/text';
import { observer } from 'mobx-react-lite';
import { getSnapshot, onSnapshot } from 'mobx-state-tree';
import { useCreateStore, useProvider } from 'mobx-store-provider';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { Node } from 'slate';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../../../hooks/useDeviceQuery.hook';
import { colorsV2 } from '../../../../../colors-v2';
import { findBellowByType } from '../../../../../DolEditor/helpers/nodeValue';
import { shadows } from '../../../../../shadows';
import {
  useBlankV2DoTestStore,
  withBlankV2DoTestStore,
} from '../../../../plugins-common/BlankV2/store/do-test.store';
import { BLANK_V2 } from '../../../../plugins-common/BlankV2/types';
import { CORRECT_ANSWER, CORRECT_ANSWERS } from '../../../../plugins-common/CorrectAnswers/types';
import {
  useExplanationsStore,
  withExplanationsStore,
} from '../../../../plugins-common/Explanations/store/explanations.store';
import { useDoExerciseProgressStore } from '../../../../shared-components/Exercise/do-exercise-progress-store.store';
import { ExerciseInCompleteNotice } from '../../../../shared-components/Exercise/ExerciseInCompleteNotice';
import { useExerciseMultipleChoiceAdminContext } from '../../../ExerciseMultipleChoice/components/AdminContext';
import { getNodeText } from '../../../shared-transforms';
import { ExerciseGapFillWithHintDoTestStore } from '../../store/exercise-gap-fill-with-hint-do-test.store';
import {
  EXERCISE_GAP_FILL_WITH_HINT_QUESTION_OPTION,
  EXERCISE_GAP_FILL_WITH_HINT_QUESTION_OPTIONS,
  ExerciseGapFillWitHintOptions,
} from '../../types';
import { withRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';
import { flatten } from 'lodash';

type DoTestExerciseGapFillProps = RenderElementProps &
  Pick<ExerciseGapFillWitHintOptions, 'onBackWhenInComplete'>;

const Main = styled.div`
  padding: 12px;
  background: ${colorsV2.white100};
  border: 1px solid ${colorsV2.gray40};
  box-shadow: ${shadows.zIndex3};
  border-radius: 8px;
  overflow: hidden;
  @media ${deviceBreakpoints.fromTablet} {
    padding: 24px;
  }
`;

const DoTestExerciseGapFillWithHintBase = (props: DoTestExerciseGapFillProps) => {
  const { attributes, children, element, onBackWhenInComplete } = props;
  const adminContext = useExerciseMultipleChoiceAdminContext();
  const blankStore = useBlankV2DoTestStore();
  const pageExerciseStore = useDoExerciseProgressStore();
  const externalStore = pageExerciseStore?.gapFillWithHintStores.get(element.id);
  const explanStore = useExplanationsStore();

  const Provider = useProvider(ExerciseGapFillWithHintDoTestStore);
  const internalStore = useCreateStore(ExerciseGapFillWithHintDoTestStore, {
    id: element?.id || nanoid(),
  });

  const produceQuestionInfo = React.useCallback(() => {
    const allCorrectAnswers = flatten(
      findBellowByType(element, CORRECT_ANSWERS).map((ans) => ans.children)
    );

    const correctAnswers = {};

    for (const correctAnswer of allCorrectAnswers) {
      const answer = correctAnswer?.correctAnswer?.toLowerCase()?.trim();
      if (correctAnswer.forId)
        correctAnswers[correctAnswer.forId] = {
          id: correctAnswer.forId,
          correctAnswers: answer.includes('/')
            ? answer.split('/').map((ans) => ans.trim())
            : [answer],
        };
    }

    return correctAnswers;
  }, [element]);

  /** Set dropdown options to use on all do test element */
  const getDropDownOptions = React.useCallback(() => {
    const dropdownOptions = {};
    const [dropdownNode] = findBellowByType(element, EXERCISE_GAP_FILL_WITH_HINT_QUESTION_OPTIONS);
    dropdownNode.children.forEach((node) => {
      dropdownOptions[node.id] = {
        id: node.id,
        displayName: getNodeText(node),
      };
    });

    return dropdownOptions;
  }, [element]);
  React.useEffect(() => {
    const dropdownOptions = getDropDownOptions();
    if (externalStore) {
      externalStore.setDropdownOptions(dropdownOptions);
    }
    internalStore.setDropdownOptions(dropdownOptions || []);
  }, [element, externalStore, getDropDownOptions, internalStore]);

  /** Set data from internalStore -> externalStore */
  React.useEffect(() => {
    if (!externalStore && internalStore && pageExerciseStore) {
      pageExerciseStore.init([internalStore.preparePayload() as any]);
    }
  }, [externalStore, pageExerciseStore, internalStore, element]);

  /** Push question info to interal store */
  React.useEffect(() => {
    internalStore.setQuestionInfos(produceQuestionInfo());
  }, [produceQuestionInfo, internalStore]);

  /** Has data from outside, here, we don't need internal store anymore.
   * if the external store already has isCompleted=true (or isViewResult=true)
   * -> set appropriate data then show the reuslt
   *  */
  React.useEffect(() => {
    if (externalStore) {
      externalStore.setQuestionInfos(produceQuestionInfo());
      if (externalStore.isViewResult) {
        const userAnswerSnapShot = getSnapshot(externalStore.userAnswers);
        blankStore.setUserAnswers(userAnswerSnapShot);
        externalStore.check();
        blankStore.viewResult();
        explanStore.viewResult();
      }
    }
  }, [produceQuestionInfo, externalStore, blankStore, explanStore, externalStore?.isViewResult]);

  /** Pull from blank store, auto save progress */
  React.useEffect(() => {
    return onSnapshot(blankStore, (snapshot) => {
      if (externalStore) {
        externalStore.setUserAnswers(snapshot.userAnswers);
      }
      internalStore.setUserAnswers(snapshot.userAnswers);
    });
  }, [blankStore, externalStore, internalStore]);

  /**
   * All correctAnswers are filled and at least 1 1 blank
   */
  const { isValid, missingFields } = React.useMemo(() => {
    const requireFields = [];

    // check if any options are empty
    const options = findBellowByType(element, EXERCISE_GAP_FILL_WITH_HINT_QUESTION_OPTION);
    if (options.some((opt) => isEmpty(Node.string(opt)))) {
      requireFields.push('Option');
    }

    // Check if has answers
    const blanks = findBellowByType(element, BLANK_V2);
    const correctAnswers = findBellowByType(element, CORRECT_ANSWER);
    const anyCorrectAnswersBlank = correctAnswers.some((correctAnswer) =>
      isEmpty(correctAnswer.correctAnswer)
    );
    if (!blanks.length || anyCorrectAnswersBlank) {
      requireFields.push('Answer');
    }

    return { isValid: !requireFields.length, missingFields: requireFields };
  }, [element]);

  return (
    <Provider value={externalStore ?? internalStore}>
      <Main className="do-test-exercise-gap-fill-with-hint font-inter" {...attributes}>
        {isValid ? (
          children
        ) : adminContext ? (
          <ExerciseInCompleteNotice
            text="Gap Fill With Hints"
            onBack={onBackWhenInComplete}
            missingFields={missingFields}
          />
        ) : null}
      </Main>
    </Provider>
  );
};

export const DoTestExerciseGapFillWithHint = compose(
  withExplanationsStore,
  withBlankV2DoTestStore,
  withRootElement,
  observer
)(DoTestExerciseGapFillWithHintBase);
